import { useQuery } from "@apollo/client";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  GetPersonCareerQuery,
  GetPersonCareerQueryVariables,
} from "types/graphql-schema";

import getPersonCareerQuery from "@apps/org-settings/graphql/get-person-career-query";
import ProfileTabEmptyState from "@apps/profile/components/profile-tab-empty-state";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Loading from "@components/loading/loading";

import SettingsPageHeader from "../settings-page-header";
import useCareerRoleOptions from "../use-career-role-options";
import { CareerTrackFormContent } from "./career-track-form/career-track-form";
import CareerTrackViewSwitcher from "./career-track-view-switcher";
import {
  getRolesAndCompetenciesBreadcrumbs,
  newCareerTrackPath,
} from "./helpers";

// One person on the ladder: their track's table narrowed to their level, with
// the criteria written for them alone. Someone without a role has no table
// yet, so the page says so.
const PersonCareerView = ({ organizationId }: { organizationId: number }) => {
  const label = useLabel();
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const userId = Number(userIdParam);
  const roleOptions = useCareerRoleOptions(organizationId);
  const { data, loading, refetch } = useQuery<
    GetPersonCareerQuery,
    GetPersonCareerQueryVariables
  >(getPersonCareerQuery, {
    fetchPolicy: "network-only",
    variables: { organizationId, userId },
  });

  const user = data?.user;
  const hasNoRolesToAssign =
    !roleOptions.loading && roleOptions.options.length === 0;
  // Someone can hold more than one role: show the one picked, or the first.
  const [chosenRoleId, setChosenRoleId] = useState<number | null>(null);
  const roles = user?.currentRoles ?? [];
  const role = roles.find(({ id }) => id === chosenRoleId) ?? roles[0];
  const roleOptionFor = (item: (typeof roles)[number]) => ({
    label: `${item.title} (${item.careerTrack.title})`,
    value: item.id,
  });

  if (user && role) {
    return (
      <CareerTrackFormContent
        roleChooser={(saveFirst) =>
          roles.length > 1 && (
            <div
              className="flex items-center gap-3 mb-3 shrink-0 text-sm"
              aria-label="Person role chooser"
            >
              <span className="text-gray-600">
                {`${user.name} has multiple roles`}
              </span>
              <div className="w-72">
                <ComboboxGeneric
                  aria-label="Person role select"
                  prefix="Showing"
                  buttonProps={{ size: ButtonSize.medium }}
                  options={roles.map(roleOptionFor)}
                  value={roleOptionFor(role)}
                  onChangeValue={async (option) => {
                    // The other role opens a new form from this page's copy
                    // of their criteria. So finish saving here, and reload
                    // that copy, before switching. A failed save stays put.
                    if (!(await saveFirst())) {
                      return;
                    }
                    await refetch();
                    setChosenRoleId(option.value);
                  }}
                />
              </div>
            </div>
          )
        }
        // A different person, or the same one in another role, starts over.
        key={`${user.id}-${role.id}`}
        organizationId={organizationId}
        careerTrackIdProp={role.careerTrack.id}
        person={{
          id: user.id,
          name: user.name,
          avatar: user.avatar,
          level: role.level,
          criteria: data.userCareerCriteria,
        }}
        onTrackCreated={() => undefined}
      />
    );
  }

  return (
    <div>
      <SettingsPageHeader
        breadcrumbs={getRolesAndCompetenciesBreadcrumbs(
          organizationId,
          label,
          user?.name,
        )}
      />
      <div className="flex items-center gap-3 mb-3">
        <CareerTrackViewSwitcher
          organizationId={organizationId}
          person={
            user
              ? { id: user.id, name: user.name, avatar: user.avatar }
              : undefined
          }
        />
      </div>
      {loading ? (
        <div className="flex justify-center mt-8">
          <Loading />
        </div>
      ) : user && hasNoRolesToAssign ? (
        // Roles live on career tracks: with none to pick, the first step is
        // making a track, not assigning.
        <ProfileTabEmptyState
          title="No career tracks yet"
          body={`There aren't any roles to assign ${user.name} yet, because no career track has been created. Create a track first, then assign them one of its roles.`}
          action={
            <Button
              theme={buttonTheme.primary}
              size={ButtonSize.large}
              text="Create Track"
              to={newCareerTrackPath(organizationId)}
            />
          }
        />
      ) : user ? (
        // Picking a role refetches this page, which then shows their track.
        <ProfileTabEmptyState
          title="No role yet"
          body={`${user.name} isn't on a career track. Assign them a role to see what's expected of them here.`}
          action={
            <ComboboxGeneric
              aria-label="Assign role menu"
              loading={roleOptions.loading}
              options={roleOptions.options}
              value={null}
              searchPlaceholder="Search roles..."
              // They hold no role yet, so there's none to end.
              onChangeValue={(option) =>
                roleOptions.assignRole(userId, option.value)
              }
            >
              <ComboboxGeneric.Button
                theme={buttonTheme.primary}
                size={ButtonSize.large}
                text="Assign Role"
                disabled={roleOptions.isSaving}
              />
            </ComboboxGeneric>
          }
        />
      ) : (
        <div className="mt-8 text-center text-sm text-gray-500">
          Person not found
        </div>
      )}
    </div>
  );
};

export default PersonCareerView;
